import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { CreateShipmentRequest, Order, ShippingQuoteRequest } from "@nh/shared";
import { adminOrderKeys } from "./orders.queries";
import type { OrderDetail } from "./orders.api";
import {
  cancelShipment,
  createShipment,
  getShippingWallet,
  quoteShipping,
  refreshShipment,
  type ShippingQuote,
  type ShippingWallet,
} from "./shipping.api";

/**
 * Los hooks del envío. Toda mutación que devuelve la ORDEN actualizada
 * la escribe en la caché del detalle — la card de envío y el resto de la
 * pantalla ven el mismo documento, sin una segunda petición.
 */

function useOrderWriteback(id: string) {
  const queryClient = useQueryClient();
  return (order: Order) => {
    queryClient.setQueryData<OrderDetail>(adminOrderKeys.one(id), (prev) => ({
      order,
      purchases: prev?.purchases ?? [],
      commissions: prev?.commissions ?? [],
    }));
    void queryClient.invalidateQueries({ queryKey: adminOrderKeys.allOrders });
  };
}

export function useQuoteShipping(
  id: string,
): UseMutationResult<ShippingQuote, Error, ShippingQuoteRequest> {
  return useMutation({ mutationFn: (input) => quoteShipping(id, input) });
}

export function useCreateShipment(
  id: string,
): UseMutationResult<Order, Error, CreateShipmentRequest> {
  const writeback = useOrderWriteback(id);
  return useMutation({
    mutationFn: (input) => createShipment(id, input),
    onSuccess: writeback,
  });
}

export function useRefreshShipment(id: string): UseMutationResult<Order, Error, void> {
  const writeback = useOrderWriteback(id);
  return useMutation({
    mutationFn: () => refreshShipment(id),
    onSuccess: writeback,
  });
}

export function useCancelShipment(id: string): UseMutationResult<Order, Error, void> {
  const writeback = useOrderWriteback(id);
  return useMutation({
    mutationFn: () => cancelShipment(id),
    onSuccess: writeback,
  });
}

/**
 * El saldo del wallet de Trasso. `retry: false` y sin refetch agresivo:
 * cuando la llave no está configurada la respuesta es un 503 estable, y
 * la card lo enseña como estado, no como error pasajero.
 */
export function useShippingWallet(enabled: boolean): UseQueryResult<ShippingWallet, Error> {
  return useQuery({
    queryKey: ["admin", "shipping", "wallet"] as const,
    queryFn: getShippingWallet,
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
